import { jwtDecode } from 'jwt-decode'
import Cookie from 'universal-cookie'

export type DecodedJWT = {
  exp: number
  iat: number
  jti: string
  iss: string
  aud: string
  sub: string
  typ: string
  azp: string
  sid: string
  acr: string
  'allowed-origins': Array<string>
  realm_access: {
    roles: Array<string>
  }
  resource_access: {
    account: { roles: Array<string> }
  }
  scope: string
  email_verified: boolean
  id_customer: string
  name?: string
  preferred_username: string
  given_name: string
  family_name: string
  email: string
}

const COOKIE_KEY = 'access_token'

/**
 * Method responsible for returning the access token from cookies from the client side
 *
 * @returns Access token or null if not found
 */
export function getAccessToken() {
  const cookieStore = new Cookie(document.cookie)

  const accessToken = cookieStore.get(COOKIE_KEY)

  return accessToken || null
}

type GetAuthInformationReturn = {
  isLoggedIn: boolean
  userName?: string | null
  customerId?: string | null
  email?: string | null
}

/**
 * Method responsible for parsing the access token and returning the basic user information
 * like name and if the user is logged in or not.
 *
 * @param accessToken - Access token to be decoded
 * @returns Decoded access token information and if user is logged in or not
 */
export function getAuthInformation(
  accessToken?: string | null,
): GetAuthInformationReturn {
  if (accessToken === undefined && typeof window !== 'undefined') {
    accessToken = getAccessToken()
  }

  if (!accessToken) return { isLoggedIn: false }

  let decoded: DecodedJWT

  try {
    decoded = jwtDecode<DecodedJWT>(accessToken)
  } catch {
    return { isLoggedIn: false } as const
  }

  if (decoded.exp * 1000 < Date.now()) {
    return { isLoggedIn: false } as const
  }

  const firstName = decoded.name?.split(' ')[0]

  return {
    isLoggedIn: true,
    userName: decoded.given_name || firstName || null,
    customerId: decoded.id_customer || null,
    email: decoded.email || null,
  }
}

export type AuthInformation = ReturnType<typeof getAuthInformation>
